import { Delete as DeleteIcon } from "@mui/icons-material";
import { IconButton, TableCell, TableRow } from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { isAfter, isBefore, isSameDay, startOfDay } from "date-fns";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { NumericFormat } from "react-number-format";
import { validateDate } from "../../../../../app/common";
import CommonTextField from "../../../../../common/components/functional/commonTextField/CommonTextField";
import { Input } from "../../../editForms/salaries/CustomInputs";

export type SupplementScheduleFormValues = {
  code: string;
  rows: { date: Date | null; value: string }[];
};

interface Props {
  index: number;
  minDate: Date;
  maxDate?: Date;
  onRemove: () => void;
}

const cellSx = { pr: 0.5, pl: 0, pt: 1, pb: 0, borderBottom: "none" } as const;

const AssignmentSupplementPriceOverridesRow = ({
  index,
  minDate,
  maxDate,
  onRemove,
}: Props) => {
  const { t } = useTranslation();
  const { control, getValues } = useFormContext<SupplementScheduleFormValues>();

  const isInAssignmentRange = (date: Date): boolean => {
    const normalizedDate = startOfDay(date);
    if (isBefore(normalizedDate, minDate)) return false;
    if (maxDate && isAfter(normalizedDate, maxDate)) return false;
    return true;
  };

  const isNoDuplicateDate = (date: Date): boolean => {
    const otherRows = getValues("rows").filter((_, i) => i !== index);
    return !otherRows.some(
      (r) => r.date instanceof Date && isSameDay(r.date, date),
    );
  };

  const validateRowDate = (v: Date | null): boolean => {
    if (!v || !validateDate(v, "")) return false;
    return isInAssignmentRange(v) && isNoDuplicateDate(v);
  };

  return (
    <TableRow>
      <TableCell sx={cellSx}>
        <Controller
          name={`rows.${index}.date`}
          control={control}
          rules={{
            required: true,
            validate: validateRowDate,
          }}
          render={({ field, fieldState }) => (
            <DesktopDatePicker
              value={field.value}
              format="dd.MM.yyyy"
              minDate={minDate}
              maxDate={maxDate}
              onChange={field.onChange}
              slots={{ textField: CommonTextField }}
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: "small",
                  error: fieldState.invalid,
                },
              }}
            />
          )}
        />
      </TableCell>
      <TableCell sx={cellSx}>
        <Controller
          name={`rows.${index}.value`}
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <NumericFormat
              value={field.value}
              decimalSeparator=","
              valueIsNumericString
              customInput={Input}
              onValueChange={(values) => field.onChange(values.value)}
              size="small"
              fullWidth
              textAlign="start"
              allowNegative={false}
              decimalScale={2}
            />
          )}
        />
      </TableCell>
      <TableCell sx={cellSx} align="right">
        <IconButton
          size="small"
          aria-label={t("tesPricingOverride:deleteRow")}
          onClick={onRemove}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </TableCell>
    </TableRow>
  );
};

export default AssignmentSupplementPriceOverridesRow;
